import {
  PipelineDraftData,
  type AdvancedFilesInterface,
  type ParameterInterface,
  type PipelineInterface,
} from "@via/utils/interfaces.tsx";
import { ListProjectsResponseBodyItem } from "common";
import {
  createContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useLocalStorage } from "usehooks-ts";

interface GlobalProviderProps {
  children?: ReactNode;
}

interface IGlobalContext {
  sidebarExpanded: boolean;
  setSidebarExpanded: (sidebarExpanded: boolean) => void;
  gNum: number;
  setGNum: (gNum: number) => void;
  selectedProject: ListProjectsResponseBodyItem | null;
  setSelectedProject: (
    selectedProject: ListProjectsResponseBodyItem | null
  ) => void;
  selectedPipeline: PipelineInterface | null;
  setSelectedPipeline: (selectedPipeline: PipelineInterface | null) => void;
  scriptPipeConfig: AdvancedFilesInterface[] | [];
  setScriptPipeConfig: (
    scriptPipeConfig: AdvancedFilesInterface[] | []
  ) => void;
  mainPipeline: any | string;
  setMainPipeline: (selectedPipeline: any | string) => void;
  pipelineDraft: PipelineDraftData | null;
  setPipelineDraft: (
    pipelineDraft: PipelineDraftData | null | ((prevData) => PipelineDraftData)
  ) => void;
  parameters: ParameterInterface | null;
  setParameters: (parameters: ParameterInterface | null) => void;
  isSelectedCollectionPremium: boolean | null;
  setIsSelectedCollectionPremium: (value: boolean) => void;
  selectedCollectionId: string | null;
  setSelectedCollectionId: (collectionId: string | null) => void;
  impersonatedUserId: number | null;
  setImpersonatedUserId: (userId: number | null) => void;
  selectedOrganization: { id: string; name: string } | null;
  setSelectedOrganization: (
    selectedOrganization: { id: string; name: string } | null
  ) => void;
  showOrganizationCreateModal: boolean;
  setShowOrganizationCreateModal: (
    showOrganizationCreateModal: boolean
  ) => void;
}

const initial: IGlobalContext = {
  sidebarExpanded: true,
  setSidebarExpanded: () => {},
  gNum: 0,
  setGNum: () => 0,
  selectedProject: null,
  setSelectedProject: () => {},
  selectedPipeline: null,
  setSelectedPipeline: () => {},
  scriptPipeConfig: [],
  setScriptPipeConfig: () => [],
  mainPipeline: null,
  setMainPipeline: () => {},
  pipelineDraft: null,
  setPipelineDraft: () => {},
  parameters: null,
  setParameters: () => {},
  isSelectedCollectionPremium: false,
  setIsSelectedCollectionPremium: () => {},
  selectedCollectionId: null,
  setSelectedCollectionId: () => {},
  impersonatedUserId: null,
  setImpersonatedUserId: () => {},
  selectedOrganization: null,
  setSelectedOrganization: () => {},
  showOrganizationCreateModal: false,
  setShowOrganizationCreateModal: () => {},
};

// Create GlobalContext
const GlobalContext = createContext<IGlobalContext>(initial);

// Create the global context provider
const GlobalProvider: FC<GlobalProviderProps> = ({ children }) => {
  const [sidebarExpanded, setSidebarExpanded] = useLocalStorage<boolean>(
    "sidebar",
    initial.sidebarExpanded
  );
  const [gNum, setGNum] = useState<number>(initial.gNum);
  const [selectedProject, setSelectedProject] =
    useState<ListProjectsResponseBodyItem | null>(initial.selectedProject);
  const [selectedPipeline, setSelectedPipeline] =
    useState<PipelineInterface | null>(initial.selectedPipeline);
  const [mainPipeline, setMainPipeline] = useState<PipelineInterface | null>(
    initial.mainPipeline
  );
  const [pipelineDraft, setPipelineDraft] = useState<PipelineDraftData | null>(
    null
  );
  const [scriptPipeConfig, setScriptPipeConfig] = useState<
    AdvancedFilesInterface[]
  >(initial.scriptPipeConfig);
  const [parameters, setParameters] = useState<ParameterInterface | null>(
    initial.parameters
  );
  const [isSelectedCollectionPremium, setIsSelectedCollectionPremium] =
    useState(initial.isSelectedCollectionPremium);
  const [selectedCollectionId, setSelectedCollectionId] = useState(
    initial.selectedCollectionId
  );
  const [impersonatedUserId, setImpersonatedUserId] = useState(
    initial.impersonatedUserId
  );
  const [selectedOrganization, setSelectedOrganization] = useState<{
    id: string;
    name: string;
  } | null>(initial.selectedOrganization);
  const [showOrganizationCreateModal, setShowOrganizationCreateModal] =
    useState(initial.showOrganizationCreateModal);

  const contextValue = useMemo(
    () => ({
      sidebarExpanded,
      setSidebarExpanded,
      gNum,
      setGNum,
      selectedProject,
      setSelectedProject,
      selectedPipeline,
      setSelectedPipeline,
      scriptPipeConfig,
      setScriptPipeConfig,
      mainPipeline,
      setMainPipeline,
      pipelineDraft,
      setPipelineDraft,
      parameters,
      setParameters,
      isSelectedCollectionPremium,
      setIsSelectedCollectionPremium,
      selectedCollectionId,
      setSelectedCollectionId,
      impersonatedUserId,
      setImpersonatedUserId,
      selectedOrganization,
      setSelectedOrganization,
      showOrganizationCreateModal,
      setShowOrganizationCreateModal,
    }),
    [
      sidebarExpanded,
      gNum,
      pipelineDraft,
      mainPipeline,
      selectedProject,
      selectedPipeline,
      scriptPipeConfig,
      selectedCollectionId,
      isSelectedCollectionPremium,
      setIsSelectedCollectionPremium,
      parameters,
      setParameters,
      impersonatedUserId,
      setImpersonatedUserId,
      selectedOrganization,
      setSelectedOrganization,
      showOrganizationCreateModal,
      setShowOrganizationCreateModal,
    ]
  );

  return (
    <GlobalContext.Provider value={contextValue}>
      {children}
    </GlobalContext.Provider>
  );
};

export { GlobalContext, GlobalProvider, type IGlobalContext };
